import { isRecord } from '@/utils/objects';

/**
 * This browser tab's own id. Everyone sees a tab as one entry with one color, so the id has to
 * stay the same across reloads and reconnects (the realtime connection id changes on both) and
 * differ between tabs.
 *
 * It lives in sessionStorage, which is per tab and survives reloads. Duplicating a tab copies
 * sessionStorage, so on startup the tab asks the others whether its id is taken and picks a
 * new one if so.
 *
 * Claims carry a per-window id. A hot reload re-runs this module in the same window while the
 * old copy's BroadcastChannel is still open, so without it the old copy would answer the new
 * copy's claim and the tab would give up its own id.
 */

const STORAGE_KEY = 'cio-realtime-tab';
const CHANNEL = 'cio-realtime-tab-claims';

type ClaimMessage = { type: 'claim' | 'taken'; tab: string; from: string };

function randomId(): string {
  return crypto.randomUUID().replaceAll('-', '').slice(0, 20);
}

const newTabId = () => `tab-${randomId()}`;

/** Set once per window and kept across module reloads, so every copy of this module in one window shares it. */
function windowInstanceId(): string {
  window.__cioRealtimeWindowId ??= randomId();
  return window.__cioRealtimeWindowId;
}

function readStored(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function store(tab: string) {
  try {
    sessionStorage.setItem(STORAGE_KEY, tab);
  } catch {
    // Private mode or storage disabled: the id still holds for this page load.
  }
}

let current: string | null = null;
const listeners = new Set<(tab: string) => void>();

function setTab(tab: string) {
  current = tab;
  store(tab);
  listeners.forEach((listener) => listener(tab));
}

function parseClaim(data: unknown): ClaimMessage | null {
  if (!isRecord(data)) return null;
  const { type, tab, from } = data;
  if (typeof tab !== 'string' || typeof from !== 'string') return null;
  return type === 'claim' || type === 'taken' ? { type, tab, from } : null;
}

function watchForDuplicates(tab: string) {
  if (typeof BroadcastChannel === 'undefined') return;
  const self = windowInstanceId();
  const channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = ({ data }: MessageEvent<unknown>) => {
    const claim = parseClaim(data);
    if (claim === null || claim.from === self || claim.tab !== current) return;
    // Another tab started with our id (a duplicated tab): tell it to pick another.
    if (claim.type === 'claim')
      channel.postMessage({ type: 'taken', tab: claim.tab, from: self } satisfies ClaimMessage);
    // We were the duplicate.
    else setTab(newTabId());
  };
  channel.postMessage({ type: 'claim', tab, from: self } satisfies ClaimMessage);
}

/** This tab's id, created on first use. */
export function getTabId(): string {
  if (current !== null) return current;
  const tab = readStored() ?? newTabId();
  current = tab;
  store(tab);
  watchForDuplicates(tab);
  return tab;
}

/** Calls `listener` when this tab had to change its id (it turned out to be a duplicate). */
export function onTabIdChange(listener: (tab: string) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
